import { fetchApi, fetchApiBlob } from '@/services/api-client';

export type StudentCredentialPlatform =
  'google' | 'hmh' | 'mcgrawhill' | 'ixl' | 'boddle' | 'kidsaz' | 'epic' | 'locker' | 'wifi' | 'other';

export type StudentCredential = {
  id: string;
  studentId: string;
  platform: StudentCredentialPlatform;
  customLabel: string | null;
  username: string | null;
  hasPassword: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateStudentCredentialData = {
  platform: StudentCredentialPlatform;
  customLabel?: string;
  username?: string;
  password?: string;
  notes?: string;
};

export type UpdateStudentCredentialData = {
  customLabel?: string;
  username?: string | null;
  password?: string | null;
  notes?: string | null;
};

export const CREDENTIAL_PLATFORM_CATALOG: Record<
  Exclude<StudentCredentialPlatform, 'other'>,
  { label: string; loginUrl: string }
> = {
  google: { label: 'Gmail / Google', loginUrl: 'https://mail.google.com' },
  hmh: { label: 'HMH', loginUrl: 'https://www.hmhco.com/ui/login/' },
  /** Grade 1–5 Science; same username and password as HMH. */
  mcgrawhill: { label: 'McGraw Hill', loginUrl: 'https://my.mheducation.com/login' },
  ixl: { label: 'IXL', loginUrl: 'https://www.ixl.com/signin' },
  /** Kindergarten to Grade 3 maths and reading. */
  boddle: { label: 'Boddle Learning', loginUrl: 'https://play.boddlelearning.com' },
  /** Username is the teacher's; the password may be a picture to tap. */
  kidsaz: { label: 'Kids A-Z', loginUrl: 'https://www.kidsa-z.com/main/Login' },
  /** Username is the class code; no password. */
  epic: { label: 'Epic', loginUrl: 'https://www.getepic.com/students' },
  locker: { label: 'Locker', loginUrl: '' },
  wifi: { label: 'School WiFi', loginUrl: '' },
};

export function credentialLabel(cred: Pick<StudentCredential, 'platform' | 'customLabel'>): string {
  if (cred.platform === 'other') return cred.customLabel || 'Other';
  return CREDENTIAL_PLATFORM_CATALOG[cred.platform].label;
}

export function credentialLoginUrl(cred: Pick<StudentCredential, 'platform'>): string | null {
  if (cred.platform === 'other') return null;
  return CREDENTIAL_PLATFORM_CATALOG[cred.platform].loginUrl || null;
}

export type CredentialExportColumn = { key: string; label: string };
export type CredentialExportResult = {
  columns: CredentialExportColumn[];
  rows: Record<string, string>[];
  /** The grade of each row, in the same order — grouping by class works whether or not that
   *  column was asked for. Null for a student with no grade on file. */
  grades: (number | null)[];
};

export type CredentialExportRequest = {
  gradeLevel?: number;
  fields: string[];
  platforms: StudentCredentialPlatform[];
  /** Column keys in the order the sheet should read, left to right. */
  columnOrder?: string[];
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  /** One worksheet per grade in the workbook rather than the whole roster on one. */
  splitByGrade?: boolean;
};

export const studentCredentialsApi = {
  listForStudent: (studentId: string) => fetchApi<StudentCredential[]>(`/students/${studentId}/credentials`),
  create: (studentId: string, data: CreateStudentCredentialData) =>
    fetchApi<StudentCredential>(`/students/${studentId}/credentials`, { method: 'POST', body: JSON.stringify(data) }),
  update: (studentId: string, credId: string, data: UpdateStudentCredentialData) =>
    fetchApi<StudentCredential>(`/students/${studentId}/credentials/${credId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  remove: (studentId: string, credId: string) =>
    fetchApi<{ success: true }>(`/students/${studentId}/credentials/${credId}`, { method: 'DELETE' }),
  reveal: (studentId: string, credId: string) =>
    fetchApi<{ password: string | null }>(`/students/${studentId}/credentials/${credId}/reveal`),
  export: (body: CredentialExportRequest) =>
    fetchApi<CredentialExportResult>('/student-credentials/export', {
      method: 'POST',
      body: JSON.stringify(body),
      // Bulk password reveal across a grade decrypts row by row — allow well past the default.
      timeoutMs: 120_000,
    }),
  exportWorkbook: (body: CredentialExportRequest) =>
    fetchApiBlob('/student-credentials/export.xlsx', {
      method: 'POST',
      body: JSON.stringify(body),
      timeoutMs: 120_000,
    }),
};
